// Largest-remainder apportionment keeps whole-person allocations equal to the total.
export function apportionAudience(total, weights) {
    if (!Number.isSafeInteger(total) || total < 0 || !weights.length) return weights.map(() => 0);
    const sum = weights.reduce((value, weight) => value + Math.max(0, weight), 0);
    if (!sum) return weights.map(() => 0);
    const raw = weights.map(weight => total * (Math.max(0, weight) / sum));
    const sizes = raw.map(Math.floor);
    const order = raw.map((value, index) => ({ index, fraction: value - sizes[index] }))
        .sort((a, b) => b.fraction - a.fraction || a.index - b.index);
    const remaining = total - sizes.reduce((value, size) => value + size, 0);
    order.slice(0, remaining).forEach(({ index }) => { sizes[index]++; });
    return sizes;
}

// Redistribute overflow to markets with spare population, preserving whole-person totals.
export function apportionWithinPopulation(total, weights, capacities) {
    const sizes = capacities.map(() => 0);
    let remaining = Math.min(total, capacities.reduce((sum, capacity) => sum + capacity, 0));
    while (remaining > 0) {
        const available = capacities.map((capacity, index) => capacity > sizes[index] ? index : -1).filter(index => index >= 0);
        if (!available.length) break;
        let shares = available.map(index => weights[index]);
        if (!shares.some(Boolean)) shares = available.map(index => capacities[index] - sizes[index]);
        const additions = apportionAudience(remaining, shares);
        available.forEach((index, offset) => {
            const addition = Math.min(additions[offset], capacities[index] - sizes[index]);
            sizes[index] += addition;
            remaining -= addition;
        });
    }
    return sizes;
}

function cmaCapacities(province) {
    const capacities = province.cmas.map(cma => cma.enabled ? cma.population : 0);
    return [...capacities, Math.max(0, province.population - capacities.reduce((sum, population) => sum + population, 0))];
}

export function allocateProvince(province, size) {
    size = province.enabled === false ? 0 : Math.min(size, province.population);
    province.audience_size = size;
    const weights = province.cmas.map(cma => cma.enabled ? cma.population : 0);
    weights.push(Math.max(0, province.population - weights.reduce((sum, weight) => sum + weight, 0)));
    const sizes = apportionWithinPopulation(size, weights, cmaCapacities(province));
    province.cmas.forEach((cma, index) => { cma.audience_size = sizes[index]; });
}

export function provinceRemainder(province) {
    return province.audience_size - province.cmas.reduce((sum, cma) => sum + (cma.enabled ? cma.audience_size : 0), 0);
}

// Preserve custom CMA shares (and Other) when their province changes size.
export function resizeProvinceAllocation(province, size) {
    size = Math.min(size, province.population);
    const weights = province.cmas.map(cma => cma.enabled ? cma.audience_size : 0);
    weights.push(Math.max(0, provinceRemainder(province)));
    if (!weights.some(Boolean)) {
        allocateProvince(province, size);
        return;
    }
    const sizes = apportionWithinPopulation(size, weights, cmaCapacities(province));
    province.audience_size = size;
    province.cmas.forEach((cma, index) => { cma.audience_size = sizes[index]; });
}

export function rebalanceProvinceAllocations(provinces, editedProvince, requestedSize, total) {
    if (!editedProvince.enabled || !Number.isSafeInteger(total) || total < 0 ||
        !Number.isSafeInteger(requestedSize) || requestedSize < 0) return;
    const others = provinces.filter(province => province.enabled && province !== editedProvince);
    total = Math.min(total, provinces.reduce((sum, province) => sum + (province.enabled ? province.population : 0), 0));
    const minimum = Math.max(0, total - others.reduce((sum, province) => sum + province.population, 0));
    const size = Math.max(minimum, Math.min(requestedSize, total, editedProvince.population));
    let weights = others.map(province => province.audience_size);
    if (!weights.some(Boolean)) weights = others.map(province => province.population);
    const remaining = apportionWithinPopulation(total - size, weights, others.map(province => province.population));
    resizeProvinceAllocation(editedProvince, size);
    others.forEach((province, index) => resizeProvinceAllocation(province, remaining[index]));
    provinces.filter(province => !province.enabled).forEach(province => allocateProvince(province, 0));
}

export function rebalanceCmaAllocations(province, editedCma, requestedSize) {
    if (!province.enabled || !editedCma.enabled || !Number.isSafeInteger(requestedSize) || requestedSize < 0) return;
    const minimum = Math.max(0, province.audience_size - (province.population - editedCma.population));
    const size = Math.max(minimum, Math.min(requestedSize, province.audience_size, editedCma.population));
    const others = province.cmas.filter(cma => cma.enabled && cma !== editedCma);
    let weights = others.map(cma => cma.audience_size);
    weights.push(Math.max(0, provinceRemainder(province)));
    if (!weights.some(Boolean)) {
        weights = others.map(cma => cma.population);
        const selectedPopulation = province.cmas.reduce((sum, cma) => sum + (cma.enabled ? cma.population : 0), 0);
        weights.push(Math.max(0, province.population - selectedPopulation));
        if (!weights.some(Boolean)) weights[weights.length - 1] = 1;
    }
    const capacities = others.map(cma => cma.population);
    capacities.push(cmaCapacities(province).slice(-1)[0]);
    const sizes = apportionWithinPopulation(province.audience_size - size, weights, capacities);
    editedCma.audience_size = size;
    others.forEach((cma, index) => { cma.audience_size = sizes[index]; });
    province.cmas.filter(cma => !cma.enabled).forEach(cma => { cma.audience_size = 0; });
}

export function allocationInputError(value, total, unit) {
    const text = String(value ?? '').replace(/,/g, '').trim();
    if (!/^\d+(\.\d+)?$/.test(text)) return 'Enter a non-negative number.';
    const number = Number(text);
    if (unit === 'percent') {
        if (number > 100) return 'Percentage cannot exceed 100%.';
        if (!Number.isFinite(number)) return 'Enter a valid percentage.';
    } else {
        if (!Number.isSafeInteger(number)) return 'Enter a whole number of people.';
        if (number > total) return `Cannot exceed ${total.toLocaleString('en-US')} people.`;
    }
    return '';
}

export function capAllocationInput(value, total, unit) {
    const text = String(value ?? '').replace(/,/g, '').trim();
    if (!/^\d+(\.\d+)?$/.test(text)) return value;
    const number = Number(text);
    if (unit !== 'percent' && Number.isFinite(number) && !Number.isInteger(number)) return value;
    return Math.min(number, unit === 'percent' ? 100 : total);
}


// Province totals include their CMAs. Other is stored once as the remainder.
export function serializeAudienceAllocations(provinces) {
    return provinces.filter(province => province.enabled).map(province => ({
        mkt_id: String(province.mkt_id),
        audience_size: province.audience_size,
        other_audience_size: provinceRemainder(province),
        cmas: province.cmas.filter(cma => cma.enabled).map(cma => ({
            mkt_id: String(cma.mkt_id), audience_size: cma.audience_size,
        })),
    }));
}

export function restoreAudienceAllocations(provinces, allocations) {
    if (!Array.isArray(allocations)) return;
    provinces.forEach(province => {
        const saved = allocations.find(value => String(value.mkt_id) === String(province.mkt_id));
        province.enabled = !!saved;
        province.audience_size = saved?.audience_size ?? 0;
        province.cmas.forEach(cma => {
            const savedCma = saved?.cmas?.find(value => String(value.mkt_id) === String(cma.mkt_id));
            cma.enabled = !!savedCma;
            cma.audience_size = savedCma?.audience_size ?? 0;
        });
    });
}


export function audienceAllocationError(provinces, total) {
    if (!Number.isSafeInteger(total) || total <= 0) return 'Enter a positive whole number for total audience size.';
    if (!provinces.some(province => province.enabled)) return 'Enable at least one province or territory.';
    for (const province of provinces) {
        if (!Number.isSafeInteger(province.audience_size) || province.audience_size < 0 ||
            province.cmas.some(cma => !Number.isSafeInteger(cma.audience_size) || cma.audience_size < 0)) {
            return 'Enter a valid, non-negative whole number for each allocation.';
        }
        if (province.audience_size > province.population) return `${province.market} allocation exceeds its population.`;
        if (province.cmas.some(cma => cma.audience_size > cma.population)) return `A CMA allocation in ${province.market} exceeds its population.`;
        if ((!province.enabled && province.audience_size) || province.cmas.some(cma => !cma.enabled && cma.audience_size)) {
            return 'Disabled markets must have a zero audience allocation.';
        }
        if (provinceRemainder(province) < 0) return "CMA allocations must fit within their province's audience.";
        const selectedPopulation = province.cmas.reduce((sum, cma) => sum + (cma.enabled ? cma.population : 0), 0);
        if (provinceRemainder(province) > Math.max(0, province.population - selectedPopulation)) {
            return `Other allocation in ${province.market} exceeds its remaining population.`;
        }
    }
    if (provinces.reduce((sum, province) => sum + province.audience_size, 0) !== total) {
        return 'Market allocations must add up to the total audience size.';
    }
    return '';
}
